import { skipToken, useSuspenseQuery } from '@apollo/client';

import type { SeoLinkBoxInput } from '@xing-com/crate-common-graphql-types';
import {
  CITY,
  COMPANY,
  EMPLOYMENT_TYPE,
  ROLE,
  SKILL,
} from '@xing-com/crate-jobs-domain-serps-constants';
import type { SerpsListItem } from '@xing-com/crate-jobs-domain-serps-constants';
import { SeoLinkBoxesDocument } from '@xing-com/crate-jobs-domain-serps-graphql';
import type { SerpsRouteParams } from '@xing-com/crate-jobs-domain-serps-helpers';

import { useSerpsMatch } from './use-serps-match';

type UseSeoLinkBoxes = {
  data?: {
    kind: string;
    entries: SerpsListItem[];
  }[];
  error?: Error;
};

export const getSeoLinkBoxesVariables = (
  serpMatch: SerpsRouteParams | null
): SeoLinkBoxInput | null => {
  if (!serpMatch) return null;

  const { category, categoryValue, subcategory, subcategoryValue } = serpMatch;

  if (category === ROLE) {
    if (subcategory === CITY && subcategoryValue) {
      return {
        jobRole: categoryValue,
        location: subcategoryValue,
        serpType: 'local-jobRole',
      };
    }

    if (!subcategory) {
      return { jobRole: categoryValue, serpType: 'jobRole' };
    }
  }

  if (category === SKILL) {
    if (subcategory === CITY && subcategoryValue) {
      return {
        location: subcategoryValue,
        serpType: 'local-skill',
        skill: categoryValue,
      };
    }

    if (!subcategory) {
      return { serpType: 'skill', skill: categoryValue };
    }
  }

  if (category === CITY && !subcategory) {
    return { location: categoryValue, serpType: 'location' };
  }

  if (category === COMPANY && !subcategory) {
    return { company: categoryValue, serpType: 'company' };
  }

  if (category === EMPLOYMENT_TYPE) {
    if (!subcategory) {
      return {
        employmentType: categoryValue,
        serpType: 'employmentType',
      };
    }

    if (subcategory === ROLE && subcategoryValue) {
      return {
        employmentType: categoryValue,
        jobRole: subcategoryValue,
        serpType: 'jobRole-employmentType',
      };
    }

    if (subcategory === CITY && subcategoryValue) {
      return {
        employmentType: categoryValue,
        location: subcategoryValue,
        serpType: 'local-employmentType',
      };
    }
  }

  return null;
};

export const useSeoLinkBoxes = (): UseSeoLinkBoxes => {
  const serpMatch = useSerpsMatch();
  const variables = getSeoLinkBoxesVariables(serpMatch);

  const { data, error } = useSuspenseQuery(
    SeoLinkBoxesDocument,
    !variables
      ? skipToken
      : {
          variables,
          errorPolicy: 'all',
        }
  );

  const serialized = data?.seoLinkBoxes?.collection.map((item) => {
    if (item === null || item.entries === null) {
      return null;
    }

    return {
      kind: item.kind,
      entries: item.entries.flatMap((entry) => {
        if (!entry?.name || !entry.url) {
          return [];
        }

        return [
          {
            id: entry.name,
            link: { url: entry.url, label: entry.name },
            count: entry.jobCount || 0,
          },
        ];
      }),
    };
  });

  return { data: serialized?.filter((item) => item !== null), error };
};
